import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import matchiChat from '../../assets/images/matchi/matchi-chat.png';
import pinIcon from '../../assets/svg/pin.svg';
import { ReactComponent as SendIcon } from '../../assets/svg/send-icon-white-v2.svg';
import { ASK_ASSISTANT } from '../../graphql/ASK_ASSISTANT';

interface IAssistantUser {
  id: string;
  avatar?: { fileUrl: string } | null;
  professions?: { id: string; name: string }[] | null;
  profile?: {
    id: string;
    name: string;
    city?: string | null;
    country?: string | null;
  } | null;
}

interface IAssistantProject {
  id: string;
  title: string;
  description?: string | null;
  type?: string | null;
  genres?: { id: string; name: string }[] | null;
  locations?: { id: string; name: string }[] | null;
  author?: {
    id: string;
    avatar?: { fileUrl: string } | null;
    profile?: { id: string; name: string } | null;
  } | null;
  collaborations?: {
    id: string;
    isFound?: boolean | null;
    requiredSkills?: { id: string; name: string }[] | null;
  }[] | null;
}

interface IMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  users?: IAssistantUser[];
  projects?: IAssistantProject[];
}

const GREETING: IMessage = {
  id: 0,
  role: 'assistant',
  text: "Hi, I'm Matchi — your creative partner. I can help you brainstorm projects, find collaborators, and bring your ideas to life."
};

interface ISuggestion {
  title: React.ReactNode;
  description: string;
  query: string;
}

const SUGGESTIONS: ISuggestion[] = [
  {
    title: 'Give me project ideas',
    description: 'E.g. I want to make a short film...',
    query: 'Give me project ideas'
  },
  {
    title: 'Who should I collaborate with?',
    description: "E.g. I'm looking for an animator...",
    query: 'Who should I collaborate with?'
  },
  {
    title: 'How can I move my project forward?',
    description: 'E.g. I have a film script...',
    query: 'How can I move my project forward?'
  }
];

const MAX_SNIPPET_LENGTH = 140;

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '');

const formatGenreName = (name: string) => name.replace(/([A-Z])/g, ' $1').trim();

const formatAssistantText = (text: string) =>
  text
    .replace(/\\n/g, '\n')
    .replace(/([^\n])\s+(\d+)[).]\s+/g, '$1\n\n$2) ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .split(/\n+/)
    .filter(Boolean);

export const AssistantChat: React.FC = () => {
  const client = useApolloClient();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<IMessage[]>([GREETING]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const sendQuestion = async (text: string) => {
    const question = text.trim();
    if (!question || isLoading) return;

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: 'user', text: question }
    ]);
    setInputValue('');
    setIsLoading(true);

    try {
      const { data } = await client.query({
        query: ASK_ASSISTANT,
        variables: { question },
        fetchPolicy: 'no-cache'
      });
      const result = data?.askAssistant;
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: result?.answer ?? "Sorry, I couldn't come up with an answer for that.",
          users: result?.users ?? [],
          projects: result?.projects ?? []
        }
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: 'Oops, something went wrong on my end. Please try again in a moment.'
        }
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const renderUserCard = (user: IAssistantUser) => (
    <Link
      key={user.id}
      to={`/user/${user.id}`}
      className="assistant-result-user"
      onClick={() => setIsOpen(false)}
    >
      {user.avatar?.fileUrl ? (
        <img
          src={user.avatar.fileUrl}
          alt={user.profile?.name ?? 'User'}
          className="assistant-result-user-avatar"
        />
      ) : (
        <div className="assistant-result-user-placeholder">
          {user.profile?.name?.charAt(0).toUpperCase() ?? '?'}
        </div>
      )}
      <div className="assistant-result-user-info">
        <span className="assistant-result-user-name">{user.profile?.name}</span>
        {user.professions && user.professions.length > 0 && (
          <span className="assistant-result-user-profession">
            {user.professions.map((p) => p.name).join(', ')}
          </span>
        )}
        {(Boolean(user.profile?.city) || Boolean(user.profile?.country)) && (
          <span className="assistant-result-user-location">
            <img src={pinIcon} alt="location" />
            {[user.profile?.city, user.profile?.country].filter(Boolean).join(', ')}
          </span>
        )}
      </div>
    </Link>
  );

  const renderProjectCard = (project: IAssistantProject) => {
    const description = stripHtml(project.description ?? '');
    const snippet =
      description.length > MAX_SNIPPET_LENGTH
        ? `${description.substr(0, MAX_SNIPPET_LENGTH)}...`
        : description;
    const openRoles = (project.collaborations ?? [])
      .filter((collab) => !collab.isFound)
      .reduce<string[]>(
        (roles, collab) => [
          ...roles,
          ...(collab.requiredSkills ?? []).map((skill) => skill.name)
        ],
        []
      );

    return (
      <Link
        key={project.id}
        to={`/project/${project.id}`}
        className="assistant-result-project"
        onClick={() => setIsOpen(false)}
      >
        <span className="assistant-result-project-title">{project.title}</span>
        {(project.genres?.length ?? 0) > 0 || (project.locations?.length ?? 0) > 0 ? (
          <div className="assistant-result-project-tags">
            {(project.genres ?? []).map((genre) => (
              <span key={genre.id} className="assistant-result-project-tag">
                {formatGenreName(genre.name)}
              </span>
            ))}
            {(project.locations ?? []).map((location) => (
              <span key={location.id} className="assistant-result-project-tag is-location">
                <img src={pinIcon} alt="location" />
                {location.name}
              </span>
            ))}
          </div>
        ) : null}
        {snippet && (
          <p className="assistant-result-project-description">{snippet}</p>
        )}
        <div className="assistant-result-project-footer">
          {project.author?.profile?.name && (
            <span className="assistant-result-project-author">
              by {project.author.profile.name}
            </span>
          )}
          {openRoles.length > 0 && (
            <span className="assistant-result-project-roles">
              Looking for: {openRoles.slice(0, 3).join(', ')}
            </span>
          )}
        </div>
      </Link>
    );
  };

  return (
    <>
      <button className="assistant-trigger" onClick={() => setIsOpen(true)}>
        <img src={matchiChat} alt="Matchi" className="assistant-trigger-mascot" />
        <div className="assistant-trigger-text">
          <span className="assistant-trigger-title">Ask Matchi</span>
          <span className="assistant-trigger-hint">
            "Give me film ideas for 2026..."
          </span>
        </div>
        <span className="assistant-trigger-cta">Chat</span>
      </button>

      {isOpen && (
        <div className="modal is-active assistant-modal">
          <div className="modal-background" onClick={() => setIsOpen(false)} />
          <div className="assistant-modal-card">
            <header className="assistant-modal-header">
              <img src={matchiChat} alt="Matchi" className="assistant-modal-header-mascot" />
              <div className="assistant-modal-header-text">
                <span className="assistant-modal-header-title">Matchi</span>
                <span className="assistant-modal-header-subtitle">
                  Your MatchHat assistant
                </span>
              </div>
              <button
                className="assistant-modal-close"
                aria-label="close"
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </header>

            <div className="assistant-modal-body" ref={bodyRef}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`assistant-message ${
                    message.role === 'user' ? 'is-user' : 'is-assistant'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <img
                      src={matchiChat}
                      alt="Matchi"
                      className="assistant-message-avatar"
                    />
                  )}
                  <div className="assistant-message-content">
                    <div className="assistant-message-bubble">
                      {formatAssistantText(message.text).map((line, index) => (
                        <p key={index} className="assistant-message-line">
                          {line}
                        </p>
                      ))}
                    </div>
                    {message.users && message.users.length > 0 && (
                      <div className="assistant-results">
                        {message.users.map(renderUserCard)}
                      </div>
                    )}
                    {message.projects && message.projects.length > 0 && (
                      <div className="assistant-results">
                        {message.projects.map(renderProjectCard)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="assistant-message is-assistant">
                  <img
                    src={matchiChat}
                    alt="Matchi"
                    className="assistant-message-avatar"
                  />
                  <div className="assistant-message-bubble assistant-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              {messages.length === 1 && !isLoading && (
                <div className="assistant-suggestions">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion.query}
                      className="assistant-suggestion"
                      onClick={() => {
                        void sendQuestion(suggestion.query);
                      }}
                    >
                      <span className="assistant-suggestion-title">{suggestion.title}</span>
                      <span className="assistant-suggestion-desc">{suggestion.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <footer className="assistant-modal-footer">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask Matchi anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') sendQuestion(inputValue);
                }}
                disabled={isLoading}
              />
              <button
                className="assistant-modal-send"
                onClick={() => {
                  void sendQuestion(inputValue);
                }}
                disabled={isLoading || !inputValue.trim()}
                aria-label="Send"
              >
                <SendIcon />
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default AssistantChat;
